import { Save, UserPlus } from "lucide-react";
import { useEffect, useId, useMemo, useState, type FormEvent } from "react";
import { useApp } from "../state/AppContext";
import type { Guardian, Student } from "../types";
import { Button, InlineNotice, Modal, Toggle } from "./ui";

type StudentDraft = Omit<Student, "id" | "code" | "qrPayload" | "initials">;
type FormErrors = Record<string, string>;

interface StudentFormProps {
  open: boolean;
  student?: Student | null;
  onClose: () => void;
  onSaved?: (student: Student) => void;
}

const blankGuardian = (relation: Guardian["relation"]): Guardian => ({
  name: "",
  relation,
  phone: "",
  email: "",
  occupation: "",
});

const localToday = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

const defaultBirthDate = () => `${new Date().getFullYear() - 7}-01-01`;

const maskedMyKid = (birthDate: string) => {
  const digits = birthDate.replaceAll("-", "");
  return digits.length === 8 ? `${digits.slice(2)}-12-••••` : "••••••-••-••••";
};

const phoneIsValid = (value: string) =>
  /^(\+?6?0)?1\d[\s-]?\d{3,4}[\s-]?\d{4}$/.test(value.replace(/[()]/g, ""));

export function StudentForm({
  open,
  student = null,
  onClose,
  onSaved,
}: StudentFormProps) {
  const { session, classes, students, addStudent, updateStudent } = useApp();
  const formId = useId();
  const isEditing = Boolean(student);
  const [draft, setDraft] = useState<StudentDraft>(() => {
    const firstClass = classes[0];
    const birthDate = defaultBirthDate();
    return {
      name: "",
      classId: firstClass?.id ?? "",
      className: firstClass?.name ?? "",
      gender: "Lelaki",
      birthDate,
      myKidMasked: maskedMyKid(birthDate),
      nationality: "Malaysia",
      religion: "",
      address: "",
      guardian: blankGuardian("Ibu"),
      secondaryGuardian: blankGuardian("Penjaga"),
      emergencyContact: "",
      medical: {
        bloodType: "",
        allergies: "Tiada direkod",
        conditions: "Tiada direkod",
        notes: "Tiada arahan khas.",
      },
      attendanceRate: 100,
      active: true,
      joinedAt: localToday(),
    };
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);

  const initialDraft = useMemo<StudentDraft>(() => {
    if (student) {
      const { id: _id, code: _code, qrPayload: _qrPayload, initials: _initials, ...rest } =
        student;
      return rest;
    }
    const firstClass = classes[0];
    const birthDate = defaultBirthDate();
    return {
      name: "",
      classId: firstClass?.id ?? "",
      className: firstClass?.name ?? "",
      gender: "Lelaki",
      birthDate,
      myKidMasked: maskedMyKid(birthDate),
      nationality: "Malaysia",
      religion: "",
      address: "",
      guardian: blankGuardian("Ibu"),
      secondaryGuardian: blankGuardian("Penjaga"),
      emergencyContact: "",
      medical: {
        bloodType: "",
        allergies: "Tiada direkod",
        conditions: "Tiada direkod",
        notes: "Tiada arahan khas.",
      },
      attendanceRate: 100,
      active: true,
      joinedAt: localToday(),
    };
  }, [classes, student]);

  useEffect(() => {
    if (!open) return;
    setDraft(initialDraft);
    setErrors({});
    setSubmitError("");
    setSaving(false);
  }, [initialDraft, open]);

  const updateField = <Key extends keyof StudentDraft>(key: Key, value: StudentDraft[Key]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const updateGuardian = (
    key: "guardian" | "secondaryGuardian",
    field: keyof Guardian,
    value: string,
  ) => {
    setDraft((current) => ({
      ...current,
      [key]: { ...current[key], [field]: value },
    }));
    setErrors((current) => ({ ...current, [`${key}.${field}`]: "" }));
  };

  const validate = () => {
    const nextErrors: FormErrors = {};
    const normalizedName = draft.name.trim().replace(/\s+/g, " ");
    if (normalizedName.length < 3) nextErrors.name = "Masukkan nama penuh murid.";
    if (!draft.classId || !classes.some((item) => item.id === draft.classId)) {
      nextErrors.classId = "Pilih kelas yang sah.";
    }
    if (!draft.birthDate) {
      nextErrors.birthDate = "Masukkan tarikh lahir.";
    } else {
      const birth = new Date(`${draft.birthDate}T00:00:00`);
      const today = new Date(`${localToday()}T00:00:00`);
      const age = today.getFullYear() - birth.getFullYear();
      if (Number.isNaN(birth.getTime()) || birth > today || age > 18) {
        nextErrors.birthDate = "Semak semula tarikh lahir murid.";
      }
    }
    if (draft.address.trim().length < 8) nextErrors.address = "Masukkan alamat murid.";
    if (draft.guardian.name.trim().length < 3) {
      nextErrors["guardian.name"] = "Masukkan nama penjaga utama.";
    }
    if (!phoneIsValid(draft.guardian.phone.trim())) {
      nextErrors["guardian.phone"] = "Gunakan nombor telefon Malaysia yang sah.";
    }
    if (draft.guardian.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.guardian.email)) {
      nextErrors["guardian.email"] = "Semak format e-mel penjaga.";
    }
    if (
      draft.secondaryGuardian.phone &&
      !phoneIsValid(draft.secondaryGuardian.phone.trim())
    ) {
      nextErrors["secondaryGuardian.phone"] = "Semak nombor telefon penjaga kedua.";
    }
    if (
      draft.secondaryGuardian.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.secondaryGuardian.email)
    ) {
      nextErrors["secondaryGuardian.email"] = "Semak format e-mel penjaga kedua.";
    }
    if (draft.emergencyContact && !/^[+\d][\d\s-]{6,}$/.test(draft.emergencyContact)) {
      nextErrors.emergencyContact = "Semak nombor hubungan kecemasan.";
    }

    const duplicate = students.some(
      (item) =>
        item.id !== student?.id &&
        item.name.trim().toLocaleLowerCase("ms-MY") ===
          normalizedName.toLocaleLowerCase("ms-MY") &&
        item.birthDate === draft.birthDate,
    );
    if (duplicate) nextErrors.name = "Murid dengan nama dan tarikh lahir ini sudah didaftarkan.";

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError("");
    if (session?.role !== "admin") {
      setSubmitError("Hanya pentadbir boleh menyimpan perubahan profil murid.");
      return;
    }
    if (!validate()) return;

    const selectedClass = classes.find((item) => item.id === draft.classId);
    if (!selectedClass) return;
    setSaving(true);
    try {
      const cleanDraft: StudentDraft = {
        ...draft,
        name: draft.name.trim().replace(/\s+/g, " "),
        className: selectedClass.name,
        myKidMasked: maskedMyKid(draft.birthDate),
        address: draft.address.trim(),
        guardian: {
          ...draft.guardian,
          name: draft.guardian.name.trim(),
          phone: draft.guardian.phone.trim(),
          email: draft.guardian.email.trim(),
          occupation: draft.guardian.occupation.trim(),
        },
        secondaryGuardian: {
          ...draft.secondaryGuardian,
          name: draft.secondaryGuardian.name.trim(),
          phone: draft.secondaryGuardian.phone.trim(),
          email: draft.secondaryGuardian.email.trim(),
          occupation: draft.secondaryGuardian.occupation.trim(),
        },
        emergencyContact: draft.emergencyContact.trim(),
      };
      const saved = student
        ? {
            ...student,
            ...cleanDraft,
          }
        : addStudent(cleanDraft);
      if (student) updateStudent(saved);
      onSaved?.(saved);
      onClose();
    } catch {
      setSubmitError("Profil tidak dapat disimpan pada peranti ini. Sila cuba lagi.");
      setSaving(false);
    }
  };

  const fieldError = (key: string) =>
    errors[key] ? (
      <span className="form-field__error" role="alert">
        {errors[key]}
      </span>
    ) : null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? "Kemaskini profil murid" : "Daftar murid baharu"}
      description={
        isEditing
          ? `Perubahan disimpan terus dalam demo JosephCare untuk ${student?.code}.`
          : "Kod murid dan QR unik akan dijana secara automatik."
      }
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} type="button">
            Batal
          </Button>
          <Button
            type="submit"
            form={formId}
            icon={isEditing ? Save : UserPlus}
            loading={saving}
          >
            {isEditing ? "Simpan perubahan" : "Daftar murid"}
          </Button>
        </>
      }
    >
      {session?.role !== "admin" ? (
        <InlineNotice tone="danger" title="Akses pentadbir diperlukan">
          Akaun guru hanya boleh melihat profil murid.
        </InlineNotice>
      ) : (
        <form id={formId} className="student-form" onSubmit={handleSubmit} noValidate>
          {submitError && (
            <InlineNotice tone="danger" title="Profil belum disimpan">
              {submitError}
            </InlineNotice>
          )}

          <fieldset className="student-form__section">
            <legend>Biodata murid</legend>
            <p>Maklumat asas untuk pengenalan dan rekod sekolah.</p>
            <div className="form-grid">
              <div className="form-field form-grid__full">
                <label htmlFor={`${formId}-name`}>Nama penuh *</label>
                <input
                  id={`${formId}-name`}
                  value={draft.name}
                  onChange={(event) => updateField("name", event.target.value)}
                  aria-invalid={Boolean(errors.name)}
                  autoFocus
                  autoComplete="off"
                  placeholder="Contoh: Nur Aisyah binti Abdullah"
                />
                {fieldError("name")}
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-gender`}>Jantina *</label>
                <select
                  id={`${formId}-gender`}
                  value={draft.gender}
                  onChange={(event) =>
                    updateField("gender", event.target.value as Student["gender"])
                  }
                >
                  <option value="Lelaki">Lelaki</option>
                  <option value="Perempuan">Perempuan</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-birth`}>Tarikh lahir *</label>
                <input
                  id={`${formId}-birth`}
                  type="date"
                  max={localToday()}
                  value={draft.birthDate}
                  onChange={(event) => {
                    const birthDate = event.target.value;
                    setDraft((current) => ({
                      ...current,
                      birthDate,
                      myKidMasked: maskedMyKid(birthDate),
                    }));
                    setErrors((current) => ({ ...current, birthDate: "" }));
                  }}
                  aria-invalid={Boolean(errors.birthDate)}
                />
                {fieldError("birthDate")}
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-mykid`}>No. MyKid</label>
                <input
                  id={`${formId}-mykid`}
                  value={maskedMyKid(draft.birthDate)}
                  readOnly
                  aria-describedby={`${formId}-mykid-help`}
                />
                <span id={`${formId}-mykid-help`} className="form-field__help">
                  Disimpan dalam bentuk bertopeng untuk demo.
                </span>
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-nationality`}>Warganegara</label>
                <input
                  id={`${formId}-nationality`}
                  value={draft.nationality}
                  onChange={(event) => updateField("nationality", event.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-religion`}>Agama</label>
                <input
                  id={`${formId}-religion`}
                  value={draft.religion}
                  onChange={(event) => updateField("religion", event.target.value)}
                  placeholder="Contoh: Islam / Kristian"
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="student-form__section">
            <legend>Rekod sekolah</legend>
            <p>Penempatan kelas dan status semasa murid.</p>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor={`${formId}-class`}>Kelas *</label>
                <select
                  id={`${formId}-class`}
                  value={draft.classId}
                  onChange={(event) => {
                    const selected = classes.find((item) => item.id === event.target.value);
                    setDraft((current) => ({
                      ...current,
                      classId: event.target.value,
                      className: selected?.name ?? "",
                    }));
                    setErrors((current) => ({ ...current, classId: "" }));
                  }}
                  aria-invalid={Boolean(errors.classId)}
                >
                  {classes.map((schoolClass) => (
                    <option key={schoolClass.id} value={schoolClass.id}>
                      {schoolClass.name}
                    </option>
                  ))}
                </select>
                {fieldError("classId")}
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-joined`}>Tarikh mula sekolah</label>
                <input
                  id={`${formId}-joined`}
                  type="date"
                  value={draft.joinedAt}
                  onChange={(event) => updateField("joinedAt", event.target.value)}
                  required
                />
              </div>
              <div className="form-grid__full student-form__toggle">
                <Toggle
                  checked={draft.active}
                  onChange={(checked) => updateField("active", checked)}
                  label="Profil murid aktif"
                  description="Profil aktif boleh digunakan untuk imbasan QR dan rekod kehadiran."
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="student-form__section">
            <legend>Alamat dan penjaga utama</legend>
            <p>Digunakan untuk urusan sekolah dan hubungan kecemasan.</p>
            <div className="form-grid">
              <div className="form-field form-grid__full">
                <label htmlFor={`${formId}-address`}>Alamat rumah *</label>
                <textarea
                  id={`${formId}-address`}
                  value={draft.address}
                  onChange={(event) => updateField("address", event.target.value)}
                  aria-invalid={Boolean(errors.address)}
                  placeholder="Alamat penuh termasuk poskod"
                />
                {fieldError("address")}
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-guardian-name`}>Nama penjaga *</label>
                <input
                  id={`${formId}-guardian-name`}
                  value={draft.guardian.name}
                  onChange={(event) => updateGuardian("guardian", "name", event.target.value)}
                  aria-invalid={Boolean(errors["guardian.name"])}
                />
                {fieldError("guardian.name")}
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-guardian-relation`}>Hubungan</label>
                <select
                  id={`${formId}-guardian-relation`}
                  value={draft.guardian.relation}
                  onChange={(event) =>
                    updateGuardian("guardian", "relation", event.target.value)
                  }
                >
                  <option value="Ibu">Ibu</option>
                  <option value="Bapa">Bapa</option>
                  <option value="Penjaga">Penjaga</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-guardian-phone`}>Telefon *</label>
                <input
                  id={`${formId}-guardian-phone`}
                  type="tel"
                  value={draft.guardian.phone}
                  onChange={(event) => updateGuardian("guardian", "phone", event.target.value)}
                  aria-invalid={Boolean(errors["guardian.phone"])}
                  placeholder="012-345 6789"
                />
                {fieldError("guardian.phone")}
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-guardian-email`}>E-mel</label>
                <input
                  id={`${formId}-guardian-email`}
                  type="email"
                  value={draft.guardian.email}
                  onChange={(event) => updateGuardian("guardian", "email", event.target.value)}
                  aria-invalid={Boolean(errors["guardian.email"])}
                />
                {fieldError("guardian.email")}
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-guardian-job`}>Pekerjaan</label>
                <input
                  id={`${formId}-guardian-job`}
                  value={draft.guardian.occupation}
                  onChange={(event) =>
                    updateGuardian("guardian", "occupation", event.target.value)
                  }
                />
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-emergency`}>Telefon kecemasan</label>
                <input
                  id={`${formId}-emergency`}
                  type="tel"
                  value={draft.emergencyContact}
                  onChange={(event) => updateField("emergencyContact", event.target.value)}
                  aria-invalid={Boolean(errors.emergencyContact)}
                />
                {fieldError("emergencyContact")}
              </div>
            </div>
          </fieldset>

          <fieldset className="student-form__section">
            <legend>Penjaga kedua</legend>
            <p>Pilihan kedua jika penjaga utama tidak dapat dihubungi.</p>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor={`${formId}-secondary-name`}>Nama</label>
                <input
                  id={`${formId}-secondary-name`}
                  value={draft.secondaryGuardian.name}
                  onChange={(event) =>
                    updateGuardian("secondaryGuardian", "name", event.target.value)
                  }
                />
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-secondary-relation`}>Hubungan</label>
                <select
                  id={`${formId}-secondary-relation`}
                  value={draft.secondaryGuardian.relation}
                  onChange={(event) =>
                    updateGuardian("secondaryGuardian", "relation", event.target.value)
                  }
                >
                  <option value="Ibu">Ibu</option>
                  <option value="Bapa">Bapa</option>
                  <option value="Penjaga">Penjaga</option>
                </select>
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-secondary-phone`}>Telefon</label>
                <input
                  id={`${formId}-secondary-phone`}
                  type="tel"
                  value={draft.secondaryGuardian.phone}
                  onChange={(event) =>
                    updateGuardian("secondaryGuardian", "phone", event.target.value)
                  }
                  aria-invalid={Boolean(errors["secondaryGuardian.phone"])}
                />
                {fieldError("secondaryGuardian.phone")}
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-secondary-email`}>E-mel</label>
                <input
                  id={`${formId}-secondary-email`}
                  type="email"
                  value={draft.secondaryGuardian.email}
                  onChange={(event) =>
                    updateGuardian("secondaryGuardian", "email", event.target.value)
                  }
                  aria-invalid={Boolean(errors["secondaryGuardian.email"])}
                />
                {fieldError("secondaryGuardian.email")}
              </div>
              <div className="form-field form-grid__full">
                <label htmlFor={`${formId}-secondary-job`}>Pekerjaan</label>
                <input
                  id={`${formId}-secondary-job`}
                  value={draft.secondaryGuardian.occupation}
                  onChange={(event) =>
                    updateGuardian("secondaryGuardian", "occupation", event.target.value)
                  }
                />
              </div>
            </div>
          </fieldset>

          <fieldset className="student-form__section">
            <legend>Maklumat kesihatan</legend>
            <p>Maklumat sensitif ini tertakluk kepada tetapan akses pentadbir.</p>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor={`${formId}-blood`}>Kumpulan darah</label>
                <select
                  id={`${formId}-blood`}
                  value={draft.medical.bloodType}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      medical: { ...current.medical, bloodType: event.target.value },
                    }))
                  }
                >
                  <option value="">Belum direkod</option>
                  {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-allergies`}>Alahan</label>
                <input
                  id={`${formId}-allergies`}
                  value={draft.medical.allergies}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      medical: { ...current.medical, allergies: event.target.value },
                    }))
                  }
                />
              </div>
              <div className="form-field">
                <label htmlFor={`${formId}-conditions`}>Keadaan kesihatan</label>
                <input
                  id={`${formId}-conditions`}
                  value={draft.medical.conditions}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      medical: { ...current.medical, conditions: event.target.value },
                    }))
                  }
                />
              </div>
              <div className="form-field form-grid__full">
                <label htmlFor={`${formId}-medical-notes`}>Arahan / catatan</label>
                <textarea
                  id={`${formId}-medical-notes`}
                  value={draft.medical.notes}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      medical: { ...current.medical, notes: event.target.value },
                    }))
                  }
                />
              </div>
            </div>
          </fieldset>
        </form>
      )}
    </Modal>
  );
}

